import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * THE panel. Variants:
 *
 *   default   a page panel — 2xl corners, 24px inside (16px on a phone)
 *   page      the page panel without the card's own type: text inside keeps the size and ink it inherits (a panel that holds free text)
 *   compact   a panel with 16px inside at every width: one of a row of short cards (the welcome promises)
 *   sm        a card inside a panel — lg corners, 16px inside (12px on a phone)
 *   inset     a quiet grouped box inside a panel — muted ground, no border
 *   well      inset, with 12px inside at every width and on every side: a small box of fields or facts that lives in a row of a panel
 *   snug      the page panel with 8px of its own (16px from sm): it holds one block that brings its own padding (the sign-in card)
 *   callout   sm, floating: a shadow, and it may let something hang off its edge (the feedback button's intro and its tail)
 *   dashed    an empty slot or a drop zone
 *   flush     (a prop, on any size) no padding or gap of its own: the card's sections bring theirs (a tabbed card, a folded section)
 *   feature   the ONE thing a screen is about, on solid Aura green with white type. Deep green, not
 *             the button green is the same family, but this ground is fixed in both themes (white type reads 5.34:1). One per screen at most.
 */
/**
 * The dashed-slot outline shared by an empty `Card` and `FileDropZone`, so the two never drift apart
 * on radius or border colour. A drop zone composes it with its own border weight and hover/drag states.
 */
export const DASHED_SLOT = "rounded-lg border border-dashed border-border";

/** A page panel's frame: corners, edge and ground. For the one table that is its own frame; every other panel is a `Card`. */
export const PANEL_FRAME = "rounded-2xl border border-border bg-card";

/** An inner card's frame: lg corners, edge and ground. For the one list row that is its own card (a document); every other inner card is a `Card size="sm"`. */
export const INNER_FRAME = "rounded-lg border border-border bg-card";

/** The card's own type: body size, card ink. `page` and `compact` leave the text to whatever the content sets. */
const TEXT = "text-body text-card-foreground";

const cardVariants = {
  default: `${TEXT} ${PANEL_FRAME} [--card-spacing:--spacing(4)] sm:[--card-spacing:--spacing(6)]`,
  page: `${PANEL_FRAME} [--card-spacing:--spacing(4)] sm:[--card-spacing:--spacing(6)]`,
  compact: `${PANEL_FRAME} [--card-spacing:--spacing(4)]`,
  sm: `${TEXT} ${INNER_FRAME} [--card-spacing:--spacing(3)] sm:[--card-spacing:--spacing(4)]`,
  inset: `${TEXT} rounded-lg bg-muted/60 [--card-spacing:--spacing(3)] sm:[--card-spacing:--spacing(4)]`,
  well: `${TEXT} rounded-lg bg-muted/60 px-3 [--card-spacing:--spacing(3)]`,
  snug: `${TEXT} ${PANEL_FRAME} p-2 sm:p-4 [--card-spacing:--spacing(4)] sm:[--card-spacing:--spacing(6)]`,
  callout: `${TEXT} rounded-lg border border-border bg-card px-4 shadow-lg overflow-visible [--card-spacing:--spacing(3)] sm:[--card-spacing:--spacing(4)] motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2`,
  dashed: `${TEXT} ${DASHED_SLOT} bg-transparent [--card-spacing:--spacing(4)] sm:[--card-spacing:--spacing(6)]`,
  feature: `${TEXT} rounded-lg bg-aura-deep text-white [--card-spacing:--spacing(4)] sm:[--card-spacing:--spacing(6)]`,
} as const;

function Card({
  className,
  size = "default",
  flush = false,
  ...props
}: React.ComponentProps<"div"> & { size?: keyof typeof cardVariants; flush?: boolean }) {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(
        "group/card flex flex-col gap-(--card-spacing) overflow-hidden py-(--card-spacing) has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0",
        cardVariants[size],
        flush && "gap-0 py-0",
        className
      )}
      {...props}
    />
  );
}

/**
 * A card's header. With a `CardAction` the action sits on the title's right from `sm` up; on a phone it drops under the
 * title and its line rather than squeezing them into a narrow column beside it, as the job header's controls do.
 */
function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "group/card-header @container/card-header grid grid-cols-[minmax(0,1fr)] auto-rows-min items-start gap-1 px-(--card-spacing) sm:has-data-[slot=card-action]:grid-cols-[minmax(0,1fr)_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-(--card-spacing)",
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("font-heading text-heading font-semibold text-foreground", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-description" className={cn("text-small text-muted-foreground", className)} {...props} />;
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "mt-2 justify-self-start sm:col-start-2 sm:row-span-2 sm:row-start-1 sm:mt-0 sm:self-start sm:justify-self-end",
        className
      )}
      {...props}
    />
  );
}

/** The brand-tinted icon tile beside a card's title: one definition, for every header that has one. */
function IconTile({ icon: Icon }: { icon: React.ComponentType<{ className?: string; strokeWidth?: number }> }) {
  return (
    <span
      className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
      aria-hidden
    >
      <Icon className="size-4" strokeWidth={1.75} />
    </span>
  );
}

/** A card's header with its icon in a brand-tinted tile, the title and one line under it. The one header for such cards: nothing draws its own tile. */
function CardIconHeader({
  icon: Icon,
  title,
  description,
  children,
  action,
  className,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: React.ReactNode;
  /** One line under the title. */
  description?: React.ReactNode;
  /** Anything more under the title (a measure's headline). */
  children?: React.ReactNode;
  /** The card's own controls (its Edit, or Save and Cancel): beside the title from `sm` up, under it on a phone (CardAction). */
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <CardHeader className={className}>
      <div className="flex items-start gap-3">
        <IconTile icon={Icon} />
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
          {children}
        </div>
      </div>
      {action ? <CardAction>{action}</CardAction> : null}
    </CardHeader>
  );
}

/** CardIconHeader's look at a section's scale, for the sections inside one stage card: icon tile, section title, one line. */
function SectionIconHeader({
  icon: Icon,
  title,
  description,
  className,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
}) {
  return (
    <div data-slot="section-icon-header" className={cn("flex items-start gap-3", className)}>
      <IconTile icon={Icon} />
      <div className="min-w-0">
        <h2 className="font-heading text-heading font-semibold text-foreground">{title}</h2>
        {description ? <p className="text-small text-muted-foreground">{description}</p> : null}
      </div>
    </div>
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("px-(--card-spacing)", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center border-t bg-muted/50 p-(--card-spacing)", className)}
      {...props}
    />
  );
}

export {
  CardIconHeader,
  IconTile,
  SectionIconHeader,
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
